"use client";

import React from "react";
import {useTranslation} from "react-i18next";
import type {AfterCallBuildFailure} from "@/components/workflows/ai-chat/headlessAfterCallBuild";
import {HandoffActionButton, OpenWorkflowButton} from "./handoffButtons";
import type {HandoffStatus} from "./useWorkflowBuildHandoff";

interface AfterCallHandoffFooterProps {
   status: HandoffStatus;
   failure: AfterCallBuildFailure | null;
   linkedWorkflowId: string | null;
   alreadyPublished: boolean;
   onRetry: () => void;
   onOpen: (workflowId: string) => void;
}

/**
 * Outcome footer of a background after-call build: published, failed (with
 * the reason and a retry), being built in another tab, or left unfinished by
 * an earlier visit (offer to resume). Progress labels are rendered by the
 * bubble itself.
 */
const AfterCallHandoffFooter: React.FC<AfterCallHandoffFooterProps> = ({
   status,
   failure,
   linkedWorkflowId,
   alreadyPublished,
   onRetry,
   onOpen,
}) => {
   const {t} = useTranslation("ask");
   const openButton = linkedWorkflowId ? (
      <OpenWorkflowButton onClick={() => onOpen(linkedWorkflowId)} />
   ) : null;

   if (status === "published" || alreadyPublished) {
      return (
         <div className="px-4 pb-3 pt-1 flex flex-col gap-2">
            <p className="text-xs text-500">{t("workspace.buildWorkflow.published")}</p>
            {openButton}
         </div>
      );
   }

   if (status === "error") {
      return (
         <div className="px-4 pb-3 pt-1 flex flex-col gap-2">
            <p className="text-xs text-red-500">
               {t(`workspace.buildWorkflow.failed.${failure ?? "builder"}`)}
            </p>
            <div className="flex flex-wrap gap-2">
               <HandoffActionButton label={t("workspace.buildWorkflow.retry")} onClick={onRetry} />
               {openButton}
            </div>
         </div>
      );
   }

   if (status === "claimed") {
      return (
         <p className="px-4 pb-3 pt-1 text-xs text-500">
            {t("workspace.buildWorkflow.inProgressElsewhere")}
         </p>
      );
   }

   // A draft exists but never published (another tab, or the page was left
   // mid-build). Offer to resume rather than silently starting over.
   if (linkedWorkflowId && status === "idle") {
      return (
         <div className="px-4 pb-3 pt-1 flex flex-col gap-2">
            <p className="text-xs text-500">{t("workspace.buildWorkflow.unfinished")}</p>
            <div className="flex flex-wrap gap-2">
               <HandoffActionButton label={t("workspace.buildWorkflow.resume")} onClick={onRetry} />
               {openButton}
            </div>
         </div>
      );
   }

   return null;
};

export default AfterCallHandoffFooter;
